/**
 * W7 — Cluster Score card (Dashboard).
 *
 * Compact per-cluster optimization score (0-100) with the per-component
 * breakdown rendered as mini bars, styled to match the other Dashboard
 * stat cards. Same backend read as ClusterScoreBadge:
 *   GET /api/v1/clusters/{cluster_id}/score   (cluster_score_routes.py)
 *
 * Color thresholds match ClusterScoreBadge so the two surfaces never
 * disagree: ≥70 green / 40-69 amber / <40 red. `degraded: true` (one of
 * the inputs is >24h stale) shows "Degraded" instead of a wrong number.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const COMPONENT_LABELS = {
  utilization:       'Utilization',
  drift_penalty:     'Drift',
  realization:       'Realization',
  commitment_fit:    'Commitment fit',
  predicted_savings: 'Savings ceiling',
};

const scoreColor = (s) => {
  if (s == null) return 'text-slate-400';
  if (s >= 70) return 'text-success dark:text-success-light';
  if (s >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

const barColor = (s) => {
  if (s == null) return 'bg-slate-300';
  if (s >= 70) return 'bg-success';
  if (s >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

export default function ClusterScoreCard({ clusterId }) {
  const [score, setScore] = useState(null);
  const [error, setError] = useState(null);

  const fetchScore = useCallback(async () => {
    if (!clusterId) return;
    try {
      const response = await axios.get(`${API}/v1/clusters/${clusterId}/score`);
      setScore(response.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'failed to load score');
    }
  }, [clusterId]);

  useEffect(() => {
    setScore(null);
    fetchScore();
    // 5-min poll — auto_optimizer persists the cached score at the end of
    // each ~10-min cycle, so this catches an update within one cycle.
    const id = setInterval(fetchScore, 5 * 60_000);
    return () => clearInterval(id);
  }, [fetchScore]);

  const s = score?.score;
  const degraded = score?.degraded;
  const breakdown = score?.breakdown || {};

  return (
    <div
      id="tour-cluster-score"
      className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all relative overflow-hidden"
    >
      <div className="flex items-center justify-between">
        <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Cluster Score</p>
        <span className="material-symbols-outlined text-slate-400 dark:text-slate-600 text-[20px]">grade</span>
      </div>
      {error ? (
        <p className="text-xs text-red-600">Score error: {error}</p>
      ) : (
        <>
          <div className="flex items-end gap-2">
            <h3 className={`text-2xl font-bold ${degraded ? 'text-slate-400' : scoreColor(s)}`}>
              {score == null ? '—' : degraded ? 'Degraded' : Math.round(s)}
            </h3>
            {score != null && !degraded && (
              <span className="text-xs font-medium text-text-secondary mb-1">/ 100</span>
            )}
          </div>
          {degraded ? (
            <p className="text-xs text-text-secondary" title={score?.reason || ''}>
              One or more inputs is stale — score withheld
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {Object.entries(breakdown).map(([key, v]) => (
                <div key={key} className="flex items-center gap-2 text-[11px]">
                  <span className="w-24 text-slate-500 dark:text-text-secondary truncate">
                    {COMPONENT_LABELS[key] || key}
                  </span>
                  <div className="flex-1 h-1.5 bg-slate-100 dark:bg-surface-border rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor(s)}`}
                      style={{ width: `${Math.round(Math.max(0, Math.min(1, v?.value ?? 0)) * 100)}%` }}
                    />
                  </div>
                  <span className="w-8 text-right tabular-nums text-slate-600 dark:text-slate-300">
                    {v?.value == null ? '—' : `${Math.round(v.value * 100)}%`}
                  </span>
                </div>
              ))}
              {score == null && (
                <p className="text-xs text-text-secondary">Computing…</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
